import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
    Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import BlockIcon from "@mui/icons-material/Block";
import { useAuth } from "../../Context/AuthContext";
import { useNavigation } from "../../Context/NavigationContext";
import { useLoading } from "../../Context/LoadingContext";
import { ListQuery } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../components/List/ListCardHeader";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import { CustomerAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import LoadingSection from "../../components/LoadingSection";
import ResponsivePagination from "../../components/ResponsivePagination";
interface Customer {
    no: string;
    name: string;
    address: string;
    eMail: string;
    blocked: boolean;
}


const CustomerList: React.FC = () => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const { t } = useTranslation();
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    });
    const [filter, setFilter] = useState<string>("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();

    const isBooleanProperty = filterProperty === "ext_Blocked";

    useEffect(() => {
        if (isBooleanProperty) setFilter("true");
    }, [isBooleanProperty]);


    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: Customer[]; count: number }>(
                "/customerApi/GetCustomers",
                query
            );
            setCustomers(response.items);
            setTotalCustomers(response.count);
        } catch (error) {
            console.error("Failed to fetch customers:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }

        if (localFilterProperty?.trim() && filter?.trim()) {
            const newFilter = buildQueryFilter(localFilterProperty, filter);
            const isDuplicate = query.filters?.some(f =>
                f.property === newFilter.property && f.value === newFilter.value
            );
            if (isDuplicate) return;

            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
        } else {
            // No filterProperty: just update the query with the raw filter string
            setQuery({ ...query, page: 0, filter });
        }

        setFilter("");
        setFilterProperty("");
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleSelectCustomer = (customerNo: string) => {
        navigate({ path: "/sales/customer/edit", params: { customerNo } });
    };

    const handleCreateCustomer = () => {
        navigate({ path: "/sales/customer/edit", params: { customerNo: "" } });
    };

    return (
        <CenteredListCard title={t("common.customers")}>

            <Box sx={{ marginBottom: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleCreateCustomer}
                >
                     {t("common.create")}
                </Button>
            </Box>
            <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                    display: "flex",
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={5} lg={2}>
                        <TextField
                            select
                            label={t("common.field")}
                            value={filterProperty}
                            onChange={(e) => setFilterProperty(e.target.value)}
                            SelectProps={{ native: true }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        >
                            <option value="">{t("common.default")}</option>
                            <option value="ext_No">{t("common.number")}</option>
                            <option value="ext_Name">{t("common.name")}</option>
                            <option value="ext_EMail">{t("common.email")}</option>
                            <option value="ext_Blocked">{t("common.blocked")}</option>
                        </TextField>
                    </Grid>
                    <Grid item xs={7} lg={7}>
                        {(() => {
                            switch (filterProperty) {
                                case "ext_No":
                                    return (
                                        <CustomerAutoComplete onChange={(o) => setFilter(o?.value || "")} />
                                    );
                                case "ext_Blocked":
                                    return (
                                        <TextField
                                            select
                                            label={t("common.value")}
                                            variant="outlined"
                                            fullWidth
                                            onChange={(e) => setFilter(e.target.value)}
                                            value={filter}
                                            SelectProps={{ native: true }}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            <option value="true">{t("common.yes")}</option>
                                            <option value="false">{t("common.no")}</option>
                                        </TextField>
                                    );
                                default:
                                    return (
                                        <TextField
                                            label={t("common.search")}
                                            variant="outlined"
                                            fullWidth
                                            onChange={(event) => setFilter(event.target.value)}
                                            value={filter}
                                            disabled={loading}
                                        />
                                    );
                             }
                            })()}
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={loading}
                        >
                            {t("common.search")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
             {query.filters?.map((f, index) => (
                    <Chip
                        key={index}
                        label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                        onDelete={() => {
                            const newFilters = query.filters?.filter((_, i) => i !== index);
                            setQuery({ ...query, filters: newFilters, page: 0 });
                        }}
                        deleteIcon={<DeleteIcon />}
                    />
                ))}
            </Box>

            {/* Card Layout for Customers */}
         <LoadingSection loading={loading}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {customers.map((customer) => (
                    <Grid item
                        xs={12}
                        sm={customers.length === 1 ? 12 : customers.length === 2 ? 6 : 6}
                        md={customers.length === 1 ? 12 : customers.length === 2 ? 6 : 4}
                        key={customer.no}>
                        <Card
                            onClick={() => handleSelectCustomer(customer.no)}
                            sx={{
                                cursor: "pointer",
                                transition: "box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    boxShadow: 4,
                                    backgroundColor: "secondary.main",
                                },
                            }}
                        >
                            <CardContent>
                                <ListCardHeader
                                    icon={<PersonIcon fontSize="small" color="action" />}
                                    title={customer.name}
                                    status={customer.blocked
                                        ? {
                                            label: t("common.blocked"),
                                            color: "warning",
                                            icon: <BlockIcon fontSize="small" />,
                                        }
                                        : undefined}
                                />
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.number")}: {customer.no}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.address")}: {customer.address}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.email")}: {customer.eMail}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {customers.length === 0 && (
                    <Grid item xs={12}>
                            <Typography align="center"> {t("common.noEntriesFound")}</Typography>
                    </Grid>
                )}
            </Grid>
         </LoadingSection>

            {/* Pagination */}
            <ResponsivePagination

                page={query.page}

                pageSize={query.pageSize}

                totalCount={totalCustomers}

                onPageChange={handlePageChange}

            />
          
        </CenteredListCard>
    );
};

export default CustomerList;